import React, { useRef, useState } from "react";

const STORAGE_KEY = "customers.panelSplit";
const clamp = (value) => Math.min(70, Math.max(30, value));

export default function ResizableCustomerLayout({ enabled, children }) {
  const container = useRef(null);
  const dragging = useRef(false);
  const [storageError, setStorageError] = useState("");
  const [position, setPosition] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      const value = saved === null ? 50 : Number(saved);
      return Number.isFinite(value) ? clamp(value) : 50;
    } catch (error) {
      console.warn("Could not read the customer panel layout preference.", error);
      return 50;
    }
  });
  const currentPosition = useRef(position);
  const update = (value) => {
    currentPosition.current = clamp(value);
    setPosition(currentPosition.current);
  };
  const save = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(currentPosition.current));
      setStorageError("");
    } catch (error) {
      console.warn("Could not save the customer panel layout preference.", error);
      setStorageError("Panel size could not be saved in this browser.");
    }
  };
  const move = (event) => {
    const bounds = container.current.getBoundingClientRect();
    if (bounds.width > 20) update(((event.clientX - bounds.left - 10) / (bounds.width - 20)) * 100);
  };
  const finish = (event) => {
    if (!dragging.current) return;
    dragging.current = false;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    save();
  };
  const panels = React.Children.toArray(children);

  return <>
    {storageError && <p role="alert" className="mb-3 text-sm text-amber-800">{storageError}</p>}
    <div ref={container} className={`grid items-start gap-y-5 ${enabled ? "xl:grid-cols-[var(--customer-panel-columns)] xl:gap-y-0" : ""}`}
      style={{ "--customer-panel-columns": `minmax(min(20rem, calc((100% - 20px) / 2)), ${position}fr) 20px minmax(min(20rem, calc((100% - 20px) / 2)), ${100 - position}fr)` }}>
      {panels[0]}
      {enabled && <div role="separator" aria-label="Resize customer and patient record panels" aria-orientation="vertical"
        aria-valuemin={30} aria-valuemax={70} aria-valuenow={position}
        aria-valuetext={`Left panel ${Math.round(position)}%, patient record ${Math.round(100 - position)}%`}
        tabIndex={0} title="Drag or use arrow keys to resize. Double-click to reset."
        className="hidden h-full min-h-40 touch-none select-none items-center justify-center self-stretch cursor-col-resize rounded focus:outline-none focus:ring-2 focus:ring-cyan-600 xl:flex"
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          event.preventDefault();
          event.currentTarget.focus();
          event.currentTarget.setPointerCapture(event.pointerId);
          dragging.current = true;
        }}
        onPointerMove={(event) => { if (dragging.current) move(event); }}
        onPointerUp={finish} onPointerCancel={finish} onLostPointerCapture={finish}
        onDoubleClick={() => { update(50); save(); }}
        onKeyDown={(event) => {
          const next = { ArrowLeft: position - 2, ArrowRight: position + 2, Home: 30, End: 70 }[event.key];
          if (next === undefined) return;
          event.preventDefault();
          update(next);
          save();
        }}>
        <span aria-hidden="true" className="h-16 w-1 rounded-full bg-slate-300" />
      </div>}
      {panels[1]}
    </div>
  </>;
}
